import { getUserData } from "./Api";
import { getAdTag } from "./adUtm";
import trackUmamiEvent from "./umami";

const STORAGE_KEY = "pendingPaymentCheck";
// Час на возврат с платёжной страницы и проведение оплаты вебхуком
const CHECK_WINDOW_MS = 60 * 60 * 1000;
const KINDS = ["subscription", "topup"];

// Срезы хранятся раздельно по типам платежей: юзер может уйти на оплату
// и подписки, и пополнения, а заплатить только за одну из них.

function loadSnapshots() {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }
    // Миграция старого формата — одиночный срез с полем kind
    if (parsed.kind && KINDS.includes(parsed.kind)) {
      return { [parsed.kind]: parsed };
    }
    const known = {};
    for (const kind of KINDS) {
      const entry = parsed[kind];
      if (entry && typeof entry === "object" && typeof entry.at === "number") {
        known[kind] = entry;
      }
    }
    return known;
  } catch {}
  return {};
}

function saveSnapshots(snapshots) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshots));
  } catch {}
}

// Срез перед уходом на платёжную страницу: для покупки подписки — число
// подписок, для пополнения — баланс. Всё в try/catch: аналитика не должна
// помешать оплате.
export async function snapshotBeforePaymentRedirect(kind, method) {
  try {
    const [success, profile] = await getUserData();
    if (!success) {
      return;
    }
    const snapshot = { kind, method, at: Date.now() };
    if (kind === "topup") {
      snapshot.balance = Number(profile.balance) || 0;
    } else {
      snapshot.count = (profile.subs || []).length;
    }
    const snapshots = loadSnapshots();
    snapshots[kind] = snapshot;
    saveSnapshots(snapshots);
  } catch {}
}

// Проверка при открытии /account по каждому ожидающему срезу: подписок стало
// больше или баланс вырос — платёж прошёл. Если нет — срез остаётся до конца
// часа: вебхук мог ещё не дойти. Просроченные срезы выбрасываем.

// Защита от параллельных прогонов (быстрые перемонтирования страницы):
// два одновременных прогона успели бы оба увидеть срез и задублировать ивент
let isChecking = false;

export async function checkCompletedPayment() {
  if (isChecking) {
    return;
  }
  isChecking = true;
  try {
    await runPaymentCheck();
  } finally {
    isChecking = false;
  }
}

async function runPaymentCheck() {
  const snapshots = loadSnapshots();

  const now = Date.now();
  for (const kind of KINDS) {
    if (snapshots[kind] && now - snapshots[kind].at > CHECK_WINDOW_MS) {
      delete snapshots[kind];
    }
  }
  saveSnapshots(snapshots);

  const kinds = KINDS.filter((kind) => snapshots[kind]);
  if (!kinds.length) {
    return;
  }

  const [success, profile] = await getUserData();
  if (!success) {
    return;
  }

  for (const kind of kinds) {
    const snapshot = snapshots[kind];
    const isTopup = kind === "topup";
    const baseline = isTopup ? snapshot.balance : snapshot.count;
    const current = isTopup
      ? Number(profile.balance) || 0
      : (profile.subs || []).length;

    if (current > baseline) {
      delete snapshots[kind];
      saveSnapshots(snapshots);
      // Метка из хранилища (utils/adUtm): атрибуция конверсии рекламной кампании
      const utmCampaign = getAdTag();
      const utm = utmCampaign ? { utm_campaign: utmCampaign } : {};
      if (isTopup) {
        trackUmamiEvent("Payment completed", {
          type: "topup",
          method: snapshot.method,
          from: baseline,
          to: current,
          ...utm,
        });
      } else {
        // Новая подписка: специфичное событие воронки + универсальный факт оплаты
        trackUmamiEvent("Buy subscription", {
          method: snapshot.method,
          from: baseline,
          to: current,
          ...utm,
        });
        trackUmamiEvent("Payment completed", {
          type: "subscription",
          method: snapshot.method,
          from: baseline,
          to: current,
          ...utm,
        });
      }
    }
  }
}
